import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Users, Mail, User, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface InvitationDetails {
  email: string;
  familyName: string;
  invitationRole: string;
  personDetails?: {
    category: string;
    familyRole: string;
    firstName: string;
    lastName: string;
  } | null;
}

const InvitationLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string>('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (token) {
      fetchInvitationDetails();
    } else {
      setError('Token d\'invitation manquant');
      setLoading(false);
    }
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-invitation-details', {
        body: { token }
      });

      if (error) throw error;

      if (data) {
        setInvitation(data);
        setFormData(prev => ({
          ...prev,
          firstName: data.personDetails?.firstName || '',
          lastName: data.personDetails?.lastName || '',
          email: data.email || ''
        }));
      }
    } catch (err: any) {
      console.error('Error fetching invitation details:', err);
      setError('Invitation invalide ou expirée');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setSubmitting(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      setSubmitting(false);
      return;
    }

    try {
      console.log('Starting invitation signup process...', { email: formData.email });

      // Use the invitation-signup function to create user without email confirmation
      const { data: signupData, error: signupError } = await supabase.functions.invoke('invitation-signup', {
        body: {
          token,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
        },
      });

      if (signupError || !signupData?.success) {
        console.error('Invitation signup error:', signupError);
        setError(signupError?.message || "Erreur lors de la création du compte");
        setSubmitting(false);
        return;
      }

      console.log('User created successfully, signing in...');

      // Now sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) {
        console.error('Error during signin:', signInError);
        setError("Erreur lors de la connexion: " + signInError.message);
        setSubmitting(false);
        return;
      }

      console.log('Signed in successfully, confirming invitation...');

      // Confirm the invitation
      const { data: confirmData, error: confirmError } = await supabase.functions.invoke('confirm-invitation', {
        body: { token }
      });

      if (confirmError) {
        console.error('Error confirming invitation:', confirmError);
        setError("Erreur lors de la confirmation de l'invitation");
        setSubmitting(false);
        return;
      }

      // Silent invitation confirmation - no toast
      
      // Import and use app redirect
      const { redirectToApp, isRunningInApp } = await import('../lib/appRedirect');
      
      if (isRunningInApp()) {
        // Already in app, just navigate
        navigate('/dashboard');
      } else {
        // Try to redirect to app
        redirectToApp(token!);
      }

    } catch (err: any) {
      console.error('Unexpected error:', err);
      setError("Une erreur inattendue s'est produite");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">Chargement de l'invitation</h1>
            <p className="text-muted-foreground">Vérification des détails...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Invitation invalide</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto mb-4">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Rejoindre le clan</CardTitle>
          <CardDescription>
            Vous êtes invité(e) à rejoindre le clan <strong>{invitation?.familyName}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                <User className="h-4 w-4 inline mr-2" />
                Prénom
              </Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
                autoComplete="given-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">
                <User className="h-4 w-4 inline mr-2" />
                Nom
              </Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
                autoComplete="family-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="h-4 w-4 inline mr-2" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                <Lock className="h-4 w-4 inline mr-2" />
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                <Lock className="h-4 w-4 inline mr-2" />
                Confirmer le mot de passe
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>


            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création du compte...
                </>
               ) : (
                'Rejoindre le clan'
               )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationLanding;